import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Library, ArrowRight, LogIn, UserPlus, Users } from 'lucide-react';
import { sendCode, register, login, joinKb, setAuthToken } from '../api';

const MODES = [
  { key: 'register', label: '注册', icon: UserPlus, desc: '创建你的知识库' },
  { key: 'login', label: '登录', icon: LogIn, desc: '登录以继续使用' },
  { key: 'join', label: '加入', icon: Users, desc: '通过邀请码加入已有知识库' },
];

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState('register');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!phone || phone.length !== 11 || !phone.startsWith('1')) {
      setError('请输入正确的手机号');
      return;
    }
    setError('');
    setDevCode('');
    try {
      const res = await sendCode(phone);
      if (res.data.code) {
        setDevCode(res.data.code);
      }
      startCountdown();
    } catch (e) {
      setError(e.response?.data?.detail || '发送验证码失败');
    }
  };

  const handleSubmit = async () => {
    if (!phone || phone.length !== 11 || !phone.startsWith('1')) {
      setError('请输入正确的手机号');
      return;
    }
    if (!code || code.length !== 6) {
      setError('请输入6位验证码');
      return;
    }
    setError('');
    setLoading(true);
    try {
      let res;
      if (mode === 'join') {
        if (!inviteCode.trim()) {
          setError('请输入邀请码');
          setLoading(false);
          return;
        }
        res = await joinKb(phone, code, inviteCode.trim());
      } else if (mode === 'register') {
        res = await register(phone, code, inviteCode.trim() || null);
      } else {
        res = await login(phone, code);
      }
      setAuthToken(res.data.token);
      onLogin?.(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || '操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInput = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
    setPhone(val);
  };

  const handleCodeInput = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
  };

  const currentMode = MODES.find(m => m.key === mode);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="w-full max-w-sm"
      >
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4"
            style={{
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.8)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
          >
            <Library size={24} className="text-gray-700" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 tracking-tight">Argon 知识库</h1>
          <p className="text-sm text-gray-400 mt-1.5">{currentMode?.desc}</p>
        </div>

        {/* Auth Card */}
        <div
          className="rounded-2xl p-6 space-y-4"
          style={{
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.7)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
          }}
        >
          {/* Mode tabs */}
          <div className="flex bg-gray-100/60 rounded-xl p-1">
            {MODES.map(m => (
              <button
                key={m.key}
                onClick={() => { setMode(m.key); setError(''); }}
                className={`flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                  mode === m.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <m.icon size={13} />
                <span>{m.label}</span>
              </button>
            ))}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">手机号</label>
            <input
              type="tel"
              value={phone}
              onChange={handlePhoneInput}
              placeholder="请输入手机号"
              className="w-full text-sm bg-white/70 border border-gray-200/80 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:border-gray-300 transition-colors"
              style={{ backdropFilter: 'blur(4px)' }}
            />
          </div>

          {/* Verify Code */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">验证码</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={code}
                onChange={handleCodeInput}
                placeholder="6位验证码"
                className="flex-1 text-sm bg-white/70 border border-gray-200/80 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:border-gray-300 transition-colors"
                style={{ backdropFilter: 'blur(4px)' }}
              />
              <button
                onClick={handleSendCode}
                disabled={countdown > 0 || phone.length !== 11}
                className={`text-xs font-medium px-4 rounded-xl transition-all flex-shrink-0 ${
                  countdown > 0 || phone.length !== 11
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </button>
            </div>
          </div>

          {/* Dev mode: show code directly */}
          {devCode && (
            <div className="flex items-center justify-center space-x-2 bg-amber-50/80 border border-amber-200 rounded-xl px-4 py-2.5">
              <span className="text-[10px] text-amber-500 font-medium">验证码：</span>
              <code className="text-sm font-bold text-amber-700 tracking-widest">{devCode}</code>
            </div>
          )}

          {/* Invite Code — shown in register or join mode */}
          {(mode === 'register' || mode === 'join') && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                邀请码
                <span className="text-gray-400 font-normal ml-0.5">
                  {mode === 'join' ? '（必填）' : '（可选，加入已有知识库）'}
                </span>
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase().slice(0, 8))}
                placeholder="输入邀请码"
                className="w-full text-sm bg-white/70 border border-gray-200/80 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:border-gray-300 transition-colors"
                style={{ backdropFilter: 'blur(4px)' }}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 text-center">{error}</p>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading || phone.length !== 11 || code.length !== 6}
            className={`w-full text-sm font-medium py-2.5 rounded-xl flex items-center justify-center space-x-2 transition-all ${
              loading || phone.length !== 11 || code.length !== 6
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-900 text-white hover:bg-gray-800 shadow-sm'
            }`}
          >
            <span>{loading ? '处理中...' : mode === 'join' ? '加入知识库' : mode === 'register' ? '注册' : '登录'}</span>
            {!loading && <ArrowRight size={15} />}
          </button>

          {/* Toggle hint */}
          {mode !== 'join' && (
            <p className="text-xs text-center text-gray-400 pt-1">
              {mode === 'register' ? '已有账号？' : '还没有账号？'}
              <button
                onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setError(''); }}
                className="text-gray-700 font-medium ml-1 hover:underline"
              >
                {mode === 'register' ? '登录' : '注册'}
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
